import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitContactForm } from '../services/contactService';
import ErrorPage from '../components/ErrorPage';
import '../styles/Contact.css';

function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    isMember: 'yes',
    title: '',
    content: '',
    phone: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // 입력 필드가 변경될 때 해당 필드의 오류 메시지 초기화
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // 제목 검사
    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해주세요.';
    }
    
    // 내용 검사
    if (!formData.content.trim()) {
      newErrors.content = '내용을 입력해주세요.';
    }
    
    // 휴대폰 번호 검사
    const phoneRegex = /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/;
    if (!formData.phone.trim()) {
      newErrors.phone = '휴대폰 번호를 입력해주세요.';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = '올바른 휴대폰 번호 형식이 아닙니다.';
    }
    
    // 이메일 검사
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요.';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        // API 호출을 위한 데이터 준비
        const apiData = {
          title: formData.title,
          content: formData.content,
          phone: formData.phone.replace(/-/g, ''), // 하이픈 제거하여 숫자만 전송
          email: formData.email,
          isGuest: formData.isMember === 'no' // 비회원이면 true, 회원이면 false
        };

        const result = await submitContactForm(apiData);
        
        if (result.success) {
          // 성공 시 성공 메시지 표시
          setShowSuccess(true);
          
          // 5초 후 홈페이지로 리다이렉트
          setTimeout(() => {
            navigate('/');
          }, 5000);
        } else {
          // API 에러 시 에러 페이지 표시
          setApiError(result.error);
          setShowError(true);
        }
      } catch (error) {
        // 예상치 못한 에러 처리
        console.error('예상치 못한 에러:', error);
        setApiError({
          status: 0,
          message: '예상치 못한 오류가 발생했습니다.',
          errorCode: 'UNEXPECTED_ERROR'
        });
        setShowError(true);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleRetry = () => {
    setShowError(false);
    setApiError(null);
  };

  // 에러 페이지 표시
  if (showError) {
    return <ErrorPage error={apiError} onRetry={handleRetry} />;
  }

  return (
    <div className="contact-page">
      <div className="contact-container">
        {showSuccess ? (
          <div className="success-message">
            <h2>문의가 성공적으로 제출되었습니다!</h2>
            <p>5초 후 자동으로 홈페이지로 이동합니다.</p>
          </div>
        ) : (
          <>
            <h1 className="contact-title">문의하기</h1>
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group radio-group">
                <label>뻔톡 회원 여부: <span className="required">*</span></label>
                <div className="radio-options">
                  <label>
                    <input
                      type="radio"
                      name="isMember"
                      value="yes"
                      checked={formData.isMember === 'yes'}
                      onChange={handleChange}
                    />
                    회원
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="isMember"
                      value="no"
                      checked={formData.isMember === 'no'}
                      onChange={handleChange}
                    />
                    비회원
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="title">제목: <span className="required">*</span></label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  placeholder="문의 제목을 입력해주세요"
                  value={formData.title}
                  onChange={handleChange}
                  className={errors.title ? 'error' : ''}
                />
                {errors.title && <p className="error-message">{errors.title}</p>}
              </div>
              
              <div className="form-group">
                <label htmlFor="content">문의 내용: <span className="required">*</span></label>
                <textarea
                  id="content"
                  name="content"
                  rows="8"
                  placeholder="문의 내용을 자세히 입력해주세요"
                  value={formData.content}
                  onChange={handleChange}
                  className={errors.content ? 'error' : ''}
                ></textarea>
                {errors.content && <p className="error-message">{errors.content}</p>}
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">연락 가능한 휴대폰 번호: <span className="required">*</span></label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="010-0000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <p className="error-message">{errors.phone}</p>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">답변을 받으실 이메일 주소: <span className="required">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="example@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <p className="error-message">{errors.email}</p>}
              </div>
              
              <button 
                type="submit" 
                className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? '제출 중...' : '제출하기'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default Contact; 